import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext.jsx';
import { apiGet, apiPost, apiPatch, apiDelete } from '../api/client.js';

const ROLES = ['owner', 'admin', 'editor', 'viewer'];

function fmtAgo(iso) {
  if (!iso) return '—';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return iso; }
}

export default function Team({ embedded = false }) {
  const { user, organization } = useAuth();
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState({ email: '', display_name: '', role: 'editor' });
  const [addError, setAddError] = useState(null);
  const [addResult, setAddResult] = useState(null);

  const q = useQuery({
    queryKey: ['members'],
    queryFn: ({ signal }) => apiGet('/members', { signal }),
  });

  const addM = useMutation({
    mutationFn: (body) => apiPost('/members', body),
    onSuccess: (res) => {
      setAddResult(res);
      setAdding({ email: '', display_name: '', role: 'editor' });
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (err) => setAddError(err.body?.error || err.message),
  });

  const roleM = useMutation({
    mutationFn: ({ userId, role }) => apiPatch(`/members/${userId}`, { role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members'] }),
  });

  const removeM = useMutation({
    mutationFn: (userId) => apiDelete(`/members/${userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members'] }),
  });

  const isOwner = organization?.role === 'owner';
  const canManage = isOwner || organization?.role === 'admin';

  return (
    <div className={embedded ? 'account-page' : 'team-page'}>
      {!embedded && <Link to="/dashboard" className="back-link">← Dashboard</Link>}
      <header className={embedded ? 'account-page-head' : 'team-header'}>
        <h1>Team</h1>
        {embedded
          ? <p className="muted">Manage who can sign in, their roles, and remove members from {organization?.name}.</p>
          : <span className="muted">{organization?.name}</span>}
      </header>

      {canManage && (
        <section className="account-section">
          <div className="team-add-head">
            <h2>Add a member</h2>
            <button type="button" className="btn btn-compact" onClick={() => { setShowAdd((s) => !s); setAddError(null); setAddResult(null); }}>
              {showAdd ? 'Cancel' : 'Add member'}
            </button>
          </div>
          {showAdd && (
            <form
              className="team-add-form"
              onSubmit={(e) => {
                e.preventDefault();
                setAddError(null); setAddResult(null);
                addM.mutate(adding);
              }}
            >
              <label className="login-field">
                <span>Email</span>
                <input
                  type="email" required value={adding.email}
                  onChange={(e) => setAdding({ ...adding, email: e.target.value })}
                  placeholder="teammate@example.com"
                />
              </label>
              <label className="login-field">
                <span>Display name (optional)</span>
                <input
                  type="text" value={adding.display_name}
                  onChange={(e) => setAdding({ ...adding, display_name: e.target.value })}
                  placeholder="Jane Doe"
                />
              </label>
              <label className="login-field">
                <span>Role</span>
                <select
                  value={adding.role}
                  onChange={(e) => setAdding({ ...adding, role: e.target.value })}
                  className="login-field-select"
                >
                  {ROLES.filter((r) => isOwner || r !== 'owner').map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </label>
              {addError && <div className="post-action-msg err">{addError}</div>}
              <button type="submit" className="btn btn-primary" disabled={addM.isPending}>
                {addM.isPending ? 'Adding…' : 'Add member'}
              </button>
            </form>
          )}
          {addResult && (
            <div className="post-action-msg ok team-add-result">
              <div><b>{addResult.created ? 'New user created.' : 'Existing user added to org.'}</b></div>
              {addResult.temp_password && (
                <>
                  <div className="muted small">Temporary password (share securely — they should change it on first sign-in):</div>
                  <pre className="temp-password">{addResult.temp_password}</pre>
                </>
              )}
              <div className="muted small">{addResult.message}</div>
            </div>
          )}
        </section>
      )}

      <section className="account-section">
        <h2>Members</h2>
        {q.isLoading && <div className="loading">Loading…</div>}
        {q.isError && <div className="error">Couldn't load members: {q.error.message}</div>}
        {q.data && (
          <div className="team-table">
            <div className="team-row team-row-head">
              <span>Email</span>
              <span>Name</span>
              <span>Role</span>
              <span>Last sign-in</span>
              <span></span>
            </div>
            {q.data.members.map((m) => {
              const isSelf = m.id === user?.id;
              return (
                <div key={m.id} className="team-row">
                  <span className="member-email">{m.email}{isSelf && <span className="muted small"> (you)</span>}</span>
                  <span>{m.display_name || <span className="muted">—</span>}</span>
                  <span>
                    {canManage && !isSelf ? (
                      <select
                        value={m.role}
                        onChange={(e) => roleM.mutate({ userId: m.id, role: e.target.value })}
                        className="role-select"
                        disabled={roleM.isPending}
                      >
                        {ROLES.filter((r) => isOwner || r !== 'owner').map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="role-static">{m.role}</span>
                    )}
                  </span>
                  <span className="muted small">{fmtAgo(m.last_login_at)}</span>
                  <span className="team-row-actions">
                    {isOwner && !isSelf && (
                      <button
                        type="button"
                        className="btn-danger btn-compact"
                        disabled={removeM.isPending}
                        onClick={() => {
                          if (window.confirm(`Remove ${m.email} from this organization?`)) {
                            removeM.mutate(m.id);
                          }
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

